// Consumo y costo estimado de los agentes de IA: pestaña "IA" de Reportes.
//
// Espeja el patrón de AiActionsTab (mismo endpoint del microservicio de IA, sin
// el cuál la vista degrada a PendingBackendNotice en vez de mostrar error rojo):
// un solo fetch por (ventana de días), con guard de carreras y reintento.
//
// La ventana es en DÍAS (7/30), no un rango de fechas: el endpoint agrega por su
// cuenta y devuelve la serie diaria de tokens ya alineada a esos días.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Bot, RefreshCw } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "../../../../components/ui/Badge";
import { Card } from "../../../../components/ui/Card";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { Stat } from "../../../../components/ui/Stat";
import { Table, TBody, TD, TH, THead, TR } from "../../../../components/ui/Table";
import { cn } from "../../../../utils/cn";
import { useUrlState } from "../../../../hooks/useUrlState";
import { AGENT_STATUS_LABELS, AGENT_STATUS_TONES } from "../ai/agentsListHelpers";
import { ChipRadioGroup, SECONDARY_BTN_CLS } from "../ai/agentSections/primitives";
import { getAiUsage, isEndpointMissing } from "./reportsApi";
import { formatInt, ymdToLocalDate } from "./format";
import { budgetRatio, fillUsageDays, formatTokens, formatUsd } from "./aiUsageFormat";
import { useChartColors } from "./chartTheme";

// Ventanas que acepta el endpoint. La etiqueta viaja con el valor: si mañana se
// agrega una tercera, no puede mostrarse como "7 días".
const DAY_OPTIONS = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
];

// Formateadores de eje a nivel de módulo: identidad estable entre renders
// (mismo criterio que DailyTrafficChart).
const fmtDayTick = (ymd) =>
  ymdToLocalDate(ymd).toLocaleDateString("es-CL", { day: "numeric", month: "short" });
const fmtDayFull = (ymd) =>
  ymdToLocalDate(ymd).toLocaleDateString("es-CL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
const fmtTooltipValue = (value, name) => [formatTokens(value), name];

/**
 * Suma de respaldo cuando la respuesta no trae `totals` (backend a medio
 * desplegar): los KPIs de cabecera no pueden quedar en blanco si las filas por
 * agente SÍ llegaron.
 *
 * El costo se suma solo si algún agente tiene precio: si todos vienen en null,
 * el total es null ("sin precio de referencia") y no 0, que afirmaría que el
 * consumo fue gratis.
 * @param {Array<object>} agents
 * @returns {{replies: number, conversations: number, escalations: number, actions: number, tokens_in: number, tokens_out: number, cost_usd_est: number|null}}
 */
export function sumAgents(agents) {
  const rows = Array.isArray(agents) ? agents : [];
  const total = {
    replies: 0,
    conversations: 0,
    escalations: 0,
    actions: 0,
    tokens_in: 0,
    tokens_out: 0,
    cost_usd_est: null,
  };
  let cost = 0;
  let hasCost = false;

  rows.forEach((agent) => {
    total.replies += Number(agent?.replies) || 0;
    total.conversations += Number(agent?.conversations) || 0;
    total.escalations += Number(agent?.escalations) || 0;
    total.actions += Number(agent?.actions) || 0;
    total.tokens_in += Number(agent?.tokens_in) || 0;
    total.tokens_out += Number(agent?.tokens_out) || 0;
    const raw = agent?.cost_usd_est;
    // `Number(null)` es 0: hay que descartar la ausencia ANTES de coercionar, o
    // un agente sin tarifa haría que el total diga "gratis".
    if (raw !== null && raw !== undefined && Number.isFinite(Number(raw))) {
      hasCost = true;
      cost += Number(raw);
    }
  });

  if (hasCost) total.cost_usd_est = cost;
  return total;
}

/**
 * Presupuesto diario de tokens del agente como "usado/límite" + barra.
 * Sin límite configurado no se dibuja barra: una barra vacía diría "0% usado",
 * que es lo contrario de "no hay tope".
 * @param {{used: *, limit: *, agentName?: string}} props
 */
function BudgetBar({ used, limit, agentName }) {
  const { t } = useTranslation();
  const ratio = budgetRatio(used, limit);

  if (ratio === null) {
    return <span className="text-[12px] md:text-[11px] text-fg-muted">{t("Sin límite")}</span>;
  }

  const over = ratio >= 1;
  // El uso puede venir por encima del tope (excedido) o negativo (basura): el
  // ancho de la barra Y los atributos ARIA se recortan al rango, y la cifra real
  // viaja en aria-valuetext para que el lector de pantalla anuncie lo mismo que
  // el texto visible (valuenow fuera de [min,max] es ARIA inválido).
  const safeUsed = Number.isFinite(Number(used)) && Number(used) > 0 ? Number(used) : 0;
  // El nombre del agente en la etiqueta: con varias filas, un nombre accesible
  // idéntico en todas las barras no permite saber a quién pertenece cada una.
  const label = agentName
    ? t("Presupuesto de tokens de hoy de {{agente}}").replace("{{agente}}", agentName)
    : t("Presupuesto de tokens de hoy");

  return (
    <div className="min-w-[96px]">
      <span className="text-[12px] md:text-[11px] tabular-nums text-fg">
        {formatInt(safeUsed)}/{formatInt(limit)}
      </span>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(safeUsed, limit)}
        aria-valuetext={`${formatInt(safeUsed)}/${formatInt(limit)}`}
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-bg-muted"
      >
        <div
          className={cn("h-full rounded-full", over ? "bg-status-error" : "bg-brand-500")}
          // El ancho se recorta al 100%: la razón puede pasar de 1 (excedido) y
          // una barra más larga que su carril se sale de la tarjeta.
          style={{ width: `${Math.min(1, ratio) * 100}%` }}
        />
      </div>
    </div>
  );
}

// Chip de leyenda propio (círculo + label 11px): la Legend de recharts no
// respeta la tipografía ni los tokens del design system.
function LegendChip({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] md:text-[11px] text-fg-muted">
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: color }}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/**
 * Serie diaria de tokens. Recibe las filas ya rellenadas por fillUsageDays: el
 * backend manda solo los días con consumo y sin relleno la línea saltaría los
 * días de silencio.
 * @param {{rows: Array, className?: string}} props
 */
function UsageChart({ rows, className }) {
  const { t } = useTranslation();
  const { colors, axisTick, gridProps, tooltipStyle, tooltipLabelStyle } = useChartColors();

  const data = rows || [];
  const hasData = data.some((row) => (row.tokens_in || 0) > 0 || (row.tokens_out || 0) > 0);

  return (
    <Card padding="md" className={className}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
        <h2 className="text-sm font-semibold text-fg">{t("Tokens por día")}</h2>
        <div className="flex items-center gap-3">
          {/* "Tokens de entrada/salida" y no "Entrada/Salida" a secas: el merge
              de locales es PLANO entre archivos del mismo idioma, y rutificador
              define "Salida" como "Departure" — una etiqueta genérica puede
              acabar mostrando el texto de otra área de la app. */}
          <LegendChip color={colors.brand} label={t("Tokens de entrada")} />
          <LegendChip color={colors.info} label={t("Tokens de salida")} />
        </div>
      </div>

      {hasData ? (
        <div
          role="img"
          aria-label={t("Gráfico de líneas con los tokens de entrada y salida por día.")}
        >
          <ResponsiveContainer width="100%" height={220}>
            {/* left: 0 con width 48: los rótulos compactos ("1,2k") miden ~25px
                más tickMargin, y el margen negativo que usan los gráficos de
                mensajes los recortaba por la izquierda. */}
            <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={axisTick}
                tickFormatter={fmtDayTick}
                minTickGap={28}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={axisTick}
                allowDecimals={false}
                width={48}
                tickFormatter={formatTokens}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={tooltipLabelStyle}
                formatter={fmtTooltipValue}
                labelFormatter={fmtDayFull}
                cursor={{ stroke: colors.border }}
              />
              {/* Sin animación: cada cambio de ventana redibuja y el trazado de
                  1,5s de recharts se siente como parpadeo, no como feedback. */}
              <Line
                type="monotone"
                dataKey="tokens_in"
                name={t("Tokens de entrada")}
                stroke={colors.brand}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="tokens_out"
                name={t("Tokens de salida")}
                stroke={colors.info}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[220px] flex items-center justify-center text-[12px] md:text-[11px] text-fg-muted">
          {t("Sin tokens en el período.")}
        </div>
      )}
    </Card>
  );
}

// Espeja el layout real (5 KPIs + chart + tabla) para que al llegar los datos
// nada salte de sitio.
function AiUsageSkeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[104px] rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-[280px] rounded-xl" />
      <Skeleton className="h-[200px] rounded-xl" />
    </div>
  );
}

export default function AiUsagePanel({ className }) {
  const { t } = useTranslation();

  // La ventana vive en la URL (?tab=ia&aiDays=30): TabsContent desmonta el panel
  // al cambiar de pestaña, así que un useState local perdería la selección en
  // cada ida y vuelta al reporte. Cualquier valor fuera del contrato (7/30) cae
  // a 7 en vez de pedirle al backend una ventana que no acepta.
  const [daysParam, setDaysParam] = useUrlState("aiDays", 7, { type: "number" });
  const days = daysParam === 30 ? 30 : 7;
  // El valor CRUDO de la URL: el decodificado ya colapsa a 7, así que no
  // distingue "sin parámetro" de "parámetro inválido".
  const [searchParams] = useSearchParams();
  const rawDays = searchParams.get("aiDays");
  // loading → consultando; ready → hay respuesta (aunque venga vacía);
  // missing → la ruta no está desplegada (404/405); error → falló de verdad.
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Descarta respuestas de una ventana vieja: cambiar de 7 a 30 mientras la
  // consulta anterior sigue en vuelo no puede pintar los números equivocados.
  const requestIdRef = useRef(0);
  // StrictMode monta → limpia → monta sobre el MISMO fiber: el flag se re-fija
  // dentro del efecto, no solo al declarar el ref.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // `t` cambia de identidad al cambiar el idioma: si entrara a las deps del
  // load, traducir el reporte volvería a pedir el consumo (y a parpadear los
  // KPIs) sin que cambie la ventana. El mensaje de error se traduce con la
  // instancia vigente al momento del fallo.
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrent = () => aliveRef.current && requestIdRef.current === requestId;

    setState("loading");
    setError(null);
    try {
      const res = await getAiUsage(days);
      if (!isCurrent()) return;
      setData(res);
      setState("ready");
    } catch (err) {
      if (!isCurrent()) return;
      if (isEndpointMissing(err)) {
        setState("missing");
      } else {
        setError(err?.message || tRef.current("No se pudo cargar el consumo de IA."));
        setState("error");
      }
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  // Un ?aiDays fuera del contrato (15, 999, "abc") se normaliza en la URL: el
  // panel ya cae a 7, pero dejar el parámetro viejo haría que el link compartido
  // mienta sobre la ventana que se está viendo. `replace` (default del hook):
  // normalizar no es una navegación que el usuario haya pedido.
  useEffect(() => {
    if (rawDays !== null && rawDays !== String(days)) setDaysParam(days);
  }, [rawDays, days, setDaysParam]);

  const agents = useMemo(
    () => (Array.isArray(data?.agents) ? data.agents : []),
    [data],
  );

  // El backend manda los totales; si esta versión del contrato no los trae, se
  // suman localmente para que la cabecera no quede vacía.
  const totals = useMemo(() => {
    const fromApi = data?.totals;
    if (fromApi && typeof fromApi === "object" && Object.keys(fromApi).length > 0) return fromApi;
    return sumAgents(agents);
  }, [data, agents]);

  // Serie diaria rellenada a la ventana vigente (días locales, terminando hoy).
  // Se prefiere la ventana que declara la RESPUESTA: así el eje siempre cubre
  // los días que el backend agregó, aunque la URL haya cambiado mientras volaba.
  const chartRows = useMemo(
    () => fillUsageDays(data?.series, data?.days || days),
    [data, days],
  );

  const handleRetry = useCallback(() => setReloadKey((key) => key + 1), []);

  const handleDays = useCallback(
    (next) => {
      // El chip activo también emite onChange: sin esta salida, "recargar" la
      // misma ventana dejaría el estado en "loading" sin ninguna consulta en
      // vuelo, o sea un skeleton para siempre.
      if (next === days) return;
      // El estado de carga entra en el MISMO render que la ventana nueva. Si se
      // dejara al efecto, React comitearía un frame con "30 días" seleccionado y
      // la serie de 7 días rellenada con 23 días en cero que el backend nunca
      // reportó: un gráfico plano que miente sobre el mes entero.
      setState("loading");
      setDaysParam(next);
    },
    [days, setDaysParam],
  );

  const dayOptions = DAY_OPTIONS.map(({ value, label }) => ({ value, label: t(label) }));

  const totalTokens = (Number(totals.tokens_in) || 0) + (Number(totals.tokens_out) || 0);
  // Solo un número es un precio: ni null (sin tarifa) ni "" ni "0" en texto
  // pueden pintarse como US$0,0000, que afirmaría que el consumo fue gratis.
  const hasCost = typeof totals.cost_usd_est === "number" && Number.isFinite(totals.cost_usd_est);
  const isEmpty = state === "ready" && agents.length === 0;

  return (
    <section className={cn("space-y-4", className)} aria-label={t("Consumo de IA")}>
      {/* Sin endpoint no hay nada que consultar: ofrecer la ventana de días solo
          puede volver a fallar. */}
      {state !== "missing" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-fg">{t("Consumo de IA")}</h2>
          <ChipRadioGroup
            value={days}
            options={dayOptions}
            onChange={handleDays}
            ariaLabel={t("Periodo")}
          />
        </div>
      )}

      {state === "loading" && <AiUsageSkeleton />}

      {state === "missing" && (
        <PendingBackendNotice>
          {t("El consumo de IA todavía no está disponible.")}
        </PendingBackendNotice>
      )}

      {state === "error" && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
          role="alert"
        >
          <span className="flex items-center gap-1.5 text-xs text-status-error min-w-0">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {error}
          </span>
          <button type="button" onClick={handleRetry} className={SECONDARY_BTN_CLS}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            {t("Reintentar")}
          </button>
        </div>
      )}

      {isEmpty && (
        <EmptyState
          icon={<Bot className="w-6 h-6" aria-hidden="true" />}
          title={t("Sin consumo de IA en el período")}
          description={t(
            "Ningún agente de IA registró respuestas, acciones ni tokens en estos días. Prueba con una ventana más amplia.",
          )}
        />
      )}

      {state === "ready" && !isEmpty && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <Stat
              label={t("Respuestas")}
              value={formatInt(totals.replies)}
              hint={t("contestadas por IA")}
              tone="brand"
            />
            <Stat
              label={t("Casos")}
              value={formatInt(totals.conversations)}
              hint={t("atendidos por IA")}
              tone="info"
            />
            <Stat
              label={t("Acciones")}
              value={formatInt(totals.actions)}
              hint={t("ejecutadas en Beartrack")}
              tone="neutral"
            />
            <Stat
              label={t("Tokens")}
              value={formatTokens(totalTokens)}
              hint={t("entrada y salida")}
              tone="neutral"
            />
            {/* Un costo sin precio de referencia sale como raya con la razón en
                el hint: "US$0,0000" afirmaría que el consumo fue gratis. */}
            <Stat
              label={t("Costo estimado")}
              value={hasCost ? formatUsd(totals.cost_usd_est) : "—"}
              hint={hasCost ? t("estimado en USD") : t("sin precio de referencia")}
              tone="success"
            />
          </div>

          <UsageChart rows={chartRows} />

          <Card padding="md">
            <h2 className="text-sm font-semibold text-fg mb-3">{t("Consumo por agente")}</h2>
            <Table>
              <THead>
                <TR>
                  <TH>{t("Agente")}</TH>
                  <TH>{t("Proveedor / modelo")}</TH>
                  <TH>{t("Respuestas")}</TH>
                  <TH>{t("Casos")}</TH>
                  <TH>{t("Derivaciones")}</TH>
                  <TH>{t("Acciones")}</TH>
                  <TH>{t("Tokens de entrada")}</TH>
                  <TH>{t("Tokens de salida")}</TH>
                  <TH>{t("Costo")}</TH>
                  <TH>{t("Presupuesto de hoy")}</TH>
                </TR>
              </THead>
              <TBody>
                {agents.map((agent, index) => {
                  const statusKey = String(agent?.status || "").toLowerCase();
                  const statusLabel = AGENT_STATUS_LABELS[statusKey];
                  const model = [agent?.provider, agent?.model].filter(Boolean).join(" · ");
                  // El índice es el último recurso de la clave: una fila sin id
                  // ni nombre (payload fuera de contrato) duplicaría la clave y
                  // React reconciliaría por posición.
                  return (
                    <TR key={agent?.agent_id || agent?.name || index}>
                      <TD className="whitespace-nowrap">
                        <span className="flex items-center gap-2">
                          <span className="text-fg">{agent?.name || agent?.agent_id || "—"}</span>
                          {agent?.status && (
                            <Badge tone={AGENT_STATUS_TONES[statusKey] || "neutral"}>
                              {statusLabel ? t(statusLabel) : agent.status}
                            </Badge>
                          )}
                        </span>
                      </TD>
                      <TD className="whitespace-nowrap text-fg-muted">{model || "—"}</TD>
                      <TD className="tabular-nums">{formatInt(agent?.replies)}</TD>
                      <TD className="tabular-nums">{formatInt(agent?.conversations)}</TD>
                      <TD className="tabular-nums">{formatInt(agent?.escalations)}</TD>
                      <TD className="tabular-nums">{formatInt(agent?.actions)}</TD>
                      {/* El conteo exacto queda en el title: la celda compacta
                          dice el orden de magnitud, no los 7 dígitos. */}
                      <TD className="tabular-nums" title={formatInt(agent?.tokens_in)}>
                        {formatTokens(agent?.tokens_in)}
                      </TD>
                      <TD className="tabular-nums" title={formatInt(agent?.tokens_out)}>
                        {formatTokens(agent?.tokens_out)}
                      </TD>
                      <TD className="whitespace-nowrap tabular-nums">
                        {formatUsd(agent?.cost_usd_est)}
                      </TD>
                      <TD>
                        <BudgetBar
                          used={agent?.budget?.today_tokens}
                          limit={agent?.budget?.max_daily_tokens}
                          agentName={agent?.name}
                        />
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </Card>
        </div>
      )}
    </section>
  );
}
